import { z } from 'zod';
import { isValidCep, normalizeCep } from '@/domain/cep';
import type { Coords } from '@/domain/geo';

export type GeocodeOk = {
  status: 'ok';
  coords: Coords;
  street: string;
  neighborhood: string;
  city: string;
  state: string;
};
export type GeocodeFailure = {
  status: 'invalid_cep' | 'cep_not_found' | 'imprecise' | 'unavailable';
};
export type GeocodeResult = GeocodeOk | GeocodeFailure;

const VIA_CEP_BASE = 'https://viacep.com.br/ws';
const PHOTON_BASE = 'https://photon.komoot.io/api/';

/* Ask for several candidates and keep the one whose city matches, rather than trusting
   the first. Photon used to be biased to the São Paulo centre, on the premise that every
   station sat in Greater São Paulo. The network expands city by city, and measured on
   2026-08-26 that bias dragged "Avenida Brasil, Campinas" and "Rua Bahia, Campinas" onto
   the São Paulo streets of the same name, where the city guard rejected them and the lead
   read "we could not locate this CEP". The query already names the city and the state, so
   ranking by text answered 12 of 13 cases against 10, and lost none in the metro area. */
const PHOTON_CANDIDATES = 5;

// ViaCEP sends '' for a field it cannot fill, so a blank value carries no address.
const nonBlankString = z.string().refine((value) => value.trim() !== '');

const finiteNumber = z.number().refine((value) => Number.isFinite(value));

// ViaCEP answers 200 with this body when the CEP does not exist. Some records carry
// the flag as the string 'true' instead of the boolean.
const viaCepNotFoundSchema = z.object({ erro: z.union([z.literal(true), z.literal('true')]) });

/* A whole-municipality CEP has no street and no neighbourhood, so those fall back to ''.
   The municipality and the state are what the Photon query and the city guard need, so a
   body without both is unusable. */
const viaCepAddressSchema = z
  .object({
    logradouro: z.string().catch(''),
    bairro: z.string().catch(''),
    localidade: nonBlankString,
    uf: nonBlankString,
  })
  .transform((body) => ({
    street: body.logradouro,
    neighborhood: body.bairro,
    city: body.localidade,
    state: body.uf,
  }));

type PostalAddress = z.infer<typeof viaCepAddressSchema>;

const photonFeatureSchema = z.object({
  geometry: z.object({
    // GeoJSON stores coordinates as [lon, lat]. Swapping them puts São Paulo in the
    // Atlantic. A third element is the altitude, which this page does not use.
    coordinates: z.tuple([finiteNumber, finiteNumber]).rest(z.number()),
  }),
  // Photon puts the municipality in a different field depending on the OSM object it hit.
  properties: z.object({
    // A street carries its name in `name`; an address point carries it in `street`.
    name: z.string().optional(),
    street: z.string().optional(),
    city: z.string().optional(),
    district: z.string().optional(),
    county: z.string().optional(),
  }),
});

const photonResponseSchema = z.object({ features: z.array(photonFeatureSchema) });

type PhotonFeature = z.infer<typeof photonFeatureSchema>;

// 'São Paulo' and 'Sao Paulo' are the same municipality, so drop diacritics and case.
const normalizeName = (value: string): string =>
  value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const buildPhotonUrl = (address: PostalAddress, withNeighborhood: boolean): string => {
  const neighborhood = withNeighborhood ? address.neighborhood : '';
  const parts = [address.street, neighborhood, address.city, address.state, 'Brasil'].filter(
    (part) => part.trim() !== '',
  );
  const query = encodeURIComponent(parts.join(', '));
  return `${PHOTON_BASE}?q=${query}&limit=${PHOTON_CANDIDATES}`;
};

/* The comparison is strict equality on the normalized name, never a substring: 'São Paulo'
   must not accept 'São Paulo de Olivença', 900 km away. A coordinate this guard cannot
   vouch for never reaches the UI, because a wrong distance band is worse than no answer. */
const cityMatches = (feature: PhotonFeature, expectedCity: string): boolean => {
  const expected = normalizeName(expectedCity);
  if (expected === '') {
    return false;
  }
  const { city, district, county } = feature.properties;
  return [city, district, county].some(
    (name) => name !== undefined && normalizeName(name) === expected,
  );
};

// Words that say what kind of street it is, or join its name, and so identify nothing.
const STREET_FILLER = new Set([
  'rua', 'r', 'avenida', 'av', 'travessa', 'tv', 'alameda', 'al', 'estrada', 'estr',
  'praca', 'largo', 'viela', 'rodovia', 'passagem', 'beco', 'ladeira', 'via', 'viaduto',
  'de', 'da', 'do', 'das', 'dos', 'e',
]);

/* Everything after a comma is a house number, which Photon appends to an address point
   ("Rua Samuel Arnold, 763") and the Correios never carry. Digits stay, because "Rua 10"
   and "Rua 100" are different streets. */
const streetWords = (value: string): string[] =>
  normalizeName(value.split(',')[0] ?? '')
    .replace(/[^a-z0-9]+/g, ' ')
    .split(' ')
    .filter((word) => word !== '' && !STREET_FILLER.has(word));

const editDistance = (a: string, b: string): number => {
  let previous = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= b.length; j += 1) {
      current[j] = Math.min(
        (previous[j] ?? 0) + 1,
        (current[j - 1] ?? 0) + 1,
        (previous[j - 1] ?? 0) + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
    }
    previous = current;
  }
  return previous[b.length] ?? 0;
};

/* OSM and the Correios spell some names one or two letters apart ("Moraes" and "Morais",
   "Saturnino" and "Saturno"). Short words must match exactly: "Maria" and "Mario" are
   different people. */
const sameWord = (a: string, b: string): boolean =>
  a === b || (Math.min(a.length, b.length) >= 6 && editDistance(a, b) <= 2);

/* The share of words the two names have in common, counted from both sides, so a short
   name inside a longer one does not pass: "Rua Isaías" against "Rua Capitão Francisco
   Isaías de Carvalho" shares one word of five. */
const STREET_SIMILARITY = 0.75;

const namesSameStreet = (expected: string, candidate: string): boolean => {
  const wanted = streetWords(expected);
  const offered = streetWords(candidate);
  if (wanted.length === 0 || offered.length === 0) {
    return false;
  }
  const unmatched = [...offered];
  let shared = 0;
  for (const word of wanted) {
    const index = unmatched.findIndex((other) => sameWord(word, other));
    if (index !== -1) {
      unmatched.splice(index, 1);
      shared += 1;
    }
  }
  return (2 * shared) / (wanted.length + offered.length) >= STREET_SIMILARITY;
};

/* A candidate in the right city can still be the wrong street. Photon ranks by text, and
   when it does not hold the street it returns whatever matched the other words, such as a
   street named like the neighbourhood, kilometres away. A CEP without a street names a
   whole municipality and has nothing to compare. */
const streetMatches = (feature: PhotonFeature, expectedStreet: string): boolean => {
  if (expectedStreet.trim() === '') {
    return true;
  }
  const { name, street } = feature.properties;
  return [name, street].some(
    (candidate) => candidate !== undefined && namesSameStreet(expectedStreet, candidate),
  );
};

const findMatch = async (
  address: PostalAddress,
  withNeighborhood: boolean,
  fetchImpl: typeof fetch,
): Promise<PhotonFeature | 'unavailable' | undefined> => {
  const response = await fetchImpl(buildPhotonUrl(address, withNeighborhood));
  if (!response.ok) {
    return 'unavailable';
  }
  const body = photonResponseSchema.safeParse(await response.json());
  if (!body.success) {
    return undefined;
  }
  return body.data.features.find(
    (candidate) => cityMatches(candidate, address.city) && streetMatches(candidate, address.street),
  );
};

export const geocodeCep = async (
  rawCep: string,
  fetchImpl: typeof fetch = fetch,
): Promise<GeocodeResult> => {
  if (!isValidCep(rawCep)) {
    return { status: 'invalid_cep' };
  }
  try {
    const cepResponse = await fetchImpl(`${VIA_CEP_BASE}/${normalizeCep(rawCep)}/json/`);
    if (!cepResponse.ok) {
      return { status: 'unavailable' };
    }
    const cepBody: unknown = await cepResponse.json();
    if (viaCepNotFoundSchema.safeParse(cepBody).success) {
      return { status: 'cep_not_found' };
    }
    const address = viaCepAddressSchema.safeParse(cepBody);
    if (!address.success) {
      return { status: 'unavailable' };
    }
    /* The neighbourhood helps Photon tell apart streets of the same name, but the
       Correios name some neighbourhoods that OSM does not hold, and then that name
       outranks the street. Ask again without it only when no candidate passed. */
    let feature = await findMatch(address.data, true, fetchImpl);
    if (feature === undefined && address.data.neighborhood.trim() !== '') {
      feature = await findMatch(address.data, false, fetchImpl);
    }
    if (feature === 'unavailable') {
      return { status: 'unavailable' };
    }
    if (feature === undefined) {
      return { status: 'imprecise' };
    }
    const [lon, lat] = feature.geometry.coordinates;
    return { status: 'ok', coords: { lat, lon }, ...address.data };
  } catch {
    return { status: 'unavailable' };
  }
};
